<script>
import { isNullOrEmptyArray } from '@/helpers/array-helpers'
import BookingListTabKey from '@/shared/constants/booking/BookingListTabKey'
import baseMixin from '@/mixins/baseMixin'
import NoEducator from '../no-educator.vue'
import DayByDayEducator from '../day-by-day-educator.vue'
import BlockEducator from '../block-educator.vue'

export default {
  name: 'EducatorColumn',
  components: {
    NoEducator,
    DayByDayEducator,
    BlockEducator,
  },
  mixins: [baseMixin],
  props: {
    booking: { type: Object, default: null },
    currentTabName: { type: String, required: true },
    isDayByDay: { type: Boolean, default: false },
    viewSidepanel: { type: Function, required: true },
    bookingStatusLabelType: { type: Object, required: true },
  },
  emits: ['viewCandidate'],
  data() {
    return {
      BookingListTabKey,
    }
  },
  computed: {
    uniqueBlockCandidates() {
      if (this.shouldReturnSingleCandidate) {
        return this.booking.candidate ? [this.booking.candidate] : []
      }

      return this.extractUniqueCandidatesFromBlock
    },
    shouldReturnSingleCandidate() {
      const isUngrouped = this.booking.groupId === 0
      const isDayByDayWithAssignedCandidate =
        this.isDayByDay &&
        this.booking.bookingStatus !== this.bookingStatusLabelType.Open &&
        this.booking.candidate

      return isUngrouped || isDayByDayWithAssignedCandidate
    },
    extractUniqueCandidatesFromBlock() {
      const { Pending, Open } = this.bookingStatusLabelType
      const uniqueCandidatesMap = new Map()

      if (!this.booking.blockDetails) {
        return []
      }

      for (const blockBooking of this.booking.blockDetails) {
        const isAssignedStatus =
          blockBooking.bookingStatus !== Pending &&
          blockBooking.bookingStatus !== Open

        if (!isAssignedStatus) continue

        const candidate = blockBooking.candidate
        const isValidCandidate =
          candidate &&
          candidate.id != null &&
          !uniqueCandidatesMap.has(candidate.id)

        if (isValidCandidate) {
          uniqueCandidatesMap.set(candidate.id, candidate)
        }
      }

      return Array.from(uniqueCandidatesMap.values())
    },
    noEducatorsPlaced() {
      return isNullOrEmptyArray(this.uniqueBlockCandidates)
    },
    canClickEducatorTd() {
      if (this.booking.bookingStatus === this.bookingStatusLabelType.Pending)
        return true
      if (this.booking.bookingStatus === this.bookingStatusLabelType.Open)
        return true

      if (this.isDayByDay) {
        if (this.booking.candidate) return false
        else return true
      } else {
        if (this.uniqueBlockCandidates.length) return false
        else return true
      }
    },
  },
  methods: {
    viewCandidate(candidate) {
      this.$emit('viewCandidate', candidate)
    },
  },
}
</script>

<template>
  <td
    v-if="
      (!isMobile &&
        currentTabName !== BookingListTabKey.approvalRequired &&
        currentTabName !== BookingListTabKey.cancelled) ||
        (isMobile && currentTabName !== BookingListTabKey.approvalRequired)
    "
    @click.stop="canClickEducatorTd && viewSidepanel(booking)"
  >
    <div class="educator-cell">
      <!-- EDUCATOR (desktop) -->
      <template
        v-if="
          !isMobile &&
            currentTabName !== BookingListTabKey.approvalRequired &&
            currentTabName !== BookingListTabKey.cancelled
        "
      >
        <NoEducator v-if="noEducatorsPlaced" />

        <template v-else>
          <DayByDayEducator
            v-if="isDayByDay"
            :candidate="booking.candidate"
            @viewCandidate="viewCandidate"
          />

          <BlockEducator
            v-else
            :unique-block-candidates="uniqueBlockCandidates"
            @viewCandidate="viewCandidate"
          />
        </template>
      </template>

      <!-- EDUCATOR (mobile) -->
      <template
        v-if="isMobile && currentTabName !== BookingListTabKey.approvalRequired"
      >
        <div
          class="d-flex flex-row align-items-center align-center justify-content-center gap-2"
        >
          <NoEducator
            v-if="noEducatorsPlaced"
            :classification-name="booking.classification.name"
          />

          <template v-else>
            <DayByDayEducator
              v-if="isDayByDay"
              :candidate="booking.candidate"
              :classification-name="booking.classification.name"
              @viewCandidate="viewCandidate"
            />

            <!-- Block mobile: avatars only, names hidden -->
            <BlockEducator
              v-else
              :unique-block-candidates="uniqueBlockCandidates"
              :classification-name="booking.classification.name"
              @viewCandidate="viewCandidate"
            />
          </template>
        </div>
      </template>
    </div>
  </td>
</template>

<style lang="scss" scoped>
@import '@/styles/_colors.scss';
@import '@/styles/bookingList/booking-row.scss';
</style>
